import { useCallback, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setUser as updateUser } from '../../../../../apps/frontend/src/redux/slices/userSlice';
import QuestionLevelTimerModal from './QuestionLevelTimerModal';

const getCachedUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userProfile')) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Gates test-attempt navigation behind the one-time "per question timer" intro modal.
 *
 * The modal is shown when the test has `showQsLevelTimer` and the user's profile
 * has not seen it yet (`questionLevelTimerModalShown` falsy). It is marked as shown
 * on the backend as soon as the modal opens.
 */
export default function useQuestionLevelTimerGate() {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const markModalShown = useCallback(async () => {
    const user = getCachedUser();
    if (!user || user.questionLevelTimerModalShown) return;

    const updatedUser = { ...user, questionLevelTimerModalShown: true };
    localStorage.setItem('userProfile', JSON.stringify(updatedUser));
    dispatch(updateUser(updatedUser));

    try {
      await axios.patch('/api/user/profile', { questionLevelTimerModalShown: true });
    } catch (error) {
      /* non-blocking: the user still gets to start the test */
    }
  }, [dispatch]);

  /**
   * Either runs `proceed` immediately, or defers it until the user clicks
   * "Start Test" in the intro modal.
   */
  const guardAttempt = useCallback(
    (test, proceed) => {
      const user = getCachedUser();
      const shouldShow = Boolean(test?.showQsLevelTimer) && !user?.questionLevelTimerModalShown;

      if (!shouldShow) {
        proceed();
        return;
      }

      setPendingAction(() => proceed);
      setIsOpen(true);
      markModalShown();
    },
    [markModalShown],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPendingAction(null);
  }, []);

  const handleStart = useCallback(() => {
    setIsOpen(false);
    setPendingAction(null);
    if (typeof pendingAction === 'function') pendingAction();
  }, [pendingAction]);

  const modal = <QuestionLevelTimerModal open={isOpen} onClose={handleClose} onStart={handleStart} />;

  return { guardAttempt, modal };
}
